import type {
  ProArticle,
  ProArticlePricing,
  ProLineTotals,
  RemiseTier,
} from './types';

export interface TierLinePricing {
  unitHt: number;
  remisePct: number;
}

/**
 * Résolution du prix HT unitaire pour un pro.
 *
 * Reproduit `CartService.getProPrice` de Tunielec :
 *  1. Si l'overlay pro_article_pricing fournit `prix_pro_{tier}`, on l'utilise.
 *  2. Sinon, on applique `remise_{tier}` sur `article.salePriceHt`.
 *  3. Sinon, fallback final : `article.salePriceHt`.
 */
export function resolveTierUnitHt(
  article: Pick<ProArticle, 'salePriceHt'>,
  pricing: ProArticlePricing | null,
  tier: RemiseTier,
): number {
  if (pricing) {
    const direct =
      tier === 'min'
        ? pricing.prixProMin
        : tier === 'max'
          ? pricing.prixProMax
          : pricing.prixProMoy;
    if (direct != null && direct > 0) return round3(direct);
    const remise =
      tier === 'min'
        ? pricing.remiseMin
        : tier === 'max'
          ? pricing.remiseMax
          : pricing.remiseMoy;
    if (remise > 0) return round3(article.salePriceHt * (1 - remise / 100));
  }
  return round3(article.salePriceHt);
}

export function resolveTierLinePricing(
  article: Pick<ProArticle, 'salePriceHt'>,
  pricing: ProArticlePricing | null,
  tier: RemiseTier,
): TierLinePricing {
  if (!pricing) return { unitHt: round3(article.salePriceHt), remisePct: 0 };

  const direct =
    tier === 'min'
      ? pricing.prixProMin
      : tier === 'max'
        ? pricing.prixProMax
        : pricing.prixProMoy;

  if (direct != null && direct > 0) {
    return { unitHt: round3(direct), remisePct: 0 };
  }

  return {
    unitHt: round3(article.salePriceHt),
    remisePct: resolveTierRemise(pricing, tier),
  };
}

/** Resolve the discount percentage for a tier (0 if no overlay). */
export function resolveTierRemise(
  pricing: ProArticlePricing | null,
  tier: RemiseTier,
): number {
  if (!pricing) return 0;
  if (tier === 'min') return pricing.remiseMin ?? 0;
  if (tier === 'max') return pricing.remiseMax ?? 0;
  return pricing.remiseMoy ?? 0;
}

/**
 * Calcule les totaux d'une ligne :
 *  pht  = quantity * unitHt * (1 - remisePct/100)
 *  pttc = pht * (1 + vatRate/100)
 *
 * Arrondi à 3 décimales (millimes tunisiens).
 */
export function calculateProLineTotals(input: {
  quantity: number;
  unitHt: number;
  remisePct: number;
  vatRate: number;
}): ProLineTotals {
  const pre = round3(input.quantity * input.unitHt);
  const montantRemise = round3((pre * input.remisePct) / 100);
  const pht = round3(pre - montantRemise);
  const tvaAmount = round3((pht * input.vatRate) / 100);
  const pttc = round3(pht + tvaAmount);
  return { pht, pttc, montantRemise, tvaAmount };
}

/**
 * Prix unitaire HT AVANT remise, reconstitué depuis le total HT d'une ligne.
 *
 * Les lignes ProSpace (devis, commande) ne stockent pas le prix unitaire, juste
 * `pht` (total HT remise déduite). Toute reprise d'une ligne existante — calcul
 * de bénéfice, réalignement, édition backoffice — doit inverser la formule de
 * `calculateProLineTotals`, et toujours de la même façon.
 */
export function grossUnitHt(
  totalHtAfterDiscount: number,
  quantity: number,
  discountPct: number,
): number {
  if (!(quantity > 0)) return round3(totalHtAfterDiscount);
  const factor = 1 - (discountPct || 0) / 100;
  if (!(factor > 0)) return round3(totalHtAfterDiscount / quantity);
  return round3(totalHtAfterDiscount / factor / quantity);
}

export function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
